import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '@/services/axios-base-query';
import { USER_ROUTES } from '@/constants';
import { getInitials } from '@/lib';
import type {
  User,
  UserCertification,
  UserEducation,
  UserUpdatePayload,
  WorkExperience,
} from '@/types/api/user.types';

export const userApi = createApi({
  reducerPath: 'UserApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    'CurrentUser',
    'UserExperiences',
    'UserEducations',
    'UserCertifications',
    'UserSkills',
  ],
  endpoints: (builder) => ({
    getCurrentUser: builder.query<User, { tenant?: string }>({
      query: ({ tenant }) => ({
        url: USER_ROUTES.ME(tenant),
        method: 'GET',
      }),
      transformResponse: (response: User) => {
        const initials = getInitials(
          response?.profile?.first_name,
          response?.profile?.last_name
        );
        return { ...response, initials };
      },
      providesTags: ['CurrentUser'],
    }),

    updateUserProfilePic: builder.mutation<
      User['profile'],
      { tenant: string; data: FormData }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.UPDATE_USER_PROFILE_PIC(tenant),
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['CurrentUser'],
      transformErrorResponse: (response) => response,
    }),

    getUserExperiences: builder.query<WorkExperience[], { tenant: string }>({
      query: ({ tenant }) => ({
        url: USER_ROUTES.EXPERIENCES(tenant),
        method: 'GET',
      }),
      transformResponse: (response: WorkExperience[]) => response,
      providesTags: ['UserExperiences'],
    }),

    addUserExperience: builder.mutation<
      WorkExperience,
      { tenant: string; data: Partial<WorkExperience> }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.ADD_EXPERIENCE(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['UserExperiences'],
    }),

    updateUserExperience: builder.mutation<
      WorkExperience,
      { tenant: string; id: number; data: Partial<WorkExperience> }
    >({
      query: ({ tenant, id, data }) => ({
        url: USER_ROUTES.UPDATE_EXPERIENCE(tenant, id),
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['UserExperiences'],
    }),

    deleteUserExperience: builder.mutation<
      WorkExperience,
      { tenant: string; id: number }
    >({
      query: ({ tenant, id }) => ({
        url: USER_ROUTES.DELETE_EXPERIENCE(tenant, id),
        method: 'DELETE',
      }),
      invalidatesTags: ['UserExperiences'],
    }),

    getUserEducations: builder.query<UserEducation[], { tenant: string }>({
      query: ({ tenant }) => ({
        url: USER_ROUTES.GET_USER_EDUCATIONS(tenant),
        method: 'GET',
      }),
      providesTags: ['UserEducations'],
    }),

    addUserEducation: builder.mutation<
      UserEducation,
      { tenant: string; data: Partial<UserEducation> }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.ADD_USER_EDUCATIONS(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['UserEducations'],
    }),

    updateUserEducation: builder.mutation<
      UserEducation,
      { tenant: string; id: number; data: Partial<UserEducation> }
    >({
      query: ({ tenant, id, data }) => ({
        url: USER_ROUTES.UPDATE_USER_EDUCATIONS(tenant, id),
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['UserEducations'],
    }),

    deleteUserEducation: builder.mutation<
      UserEducation,
      { tenant: string; id: number }
    >({
      query: ({ tenant, id }) => ({
        url: USER_ROUTES.DELETE_USER_EDUCATIONS(tenant, id),
        method: 'DELETE',
      }),
      invalidatesTags: ['UserEducations'],
    }),

    getUserCertifications: builder.query<
      UserCertification[],
      { tenant: string }
    >({
      query: ({ tenant }) => ({
        url: USER_ROUTES.GET_USER_CERTIFICATIONS(tenant),
        method: 'GET',
      }),
      providesTags: ['UserCertifications'],
    }),

    addUserCertification: builder.mutation<
      UserCertification,
      { tenant: string; data: Partial<UserCertification> }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.ADD_CERTIFICATION(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['UserCertifications'],
    }),

    updateUserCertification: builder.mutation<
      UserCertification,
      { tenant: string; id: number; data: Partial<UserCertification> }
    >({
      query: ({ tenant, id, data }) => ({
        url: USER_ROUTES.UPDATE_USER_CERTIFICATION(tenant, id),
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['UserCertifications'],
    }),

    deleteUserCertification: builder.mutation<
      UserCertification,
      { tenant: string; id: number }
    >({
      query: ({ tenant, id }) => ({
        url: USER_ROUTES.DELETE_USER_CERTIFICATION(tenant, id),
        method: 'DELETE',
      }),
      invalidatesTags: ['UserCertifications'],
    }),

    updateUserProfile: builder.mutation<
      User,
      { tenant: string; data: Partial<UserUpdatePayload> }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.UPDATE_USER_PROFILE(tenant),
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['CurrentUser'],
    }),

    requestDeleteAccountToken: builder.mutation<void, { email: string }>({
      query: (data) => ({
        url: USER_ROUTES.REQUEST_DELETE_ACCOUNT_TOKEN,
        method: 'POST',
        data,
      }),
    }),

    verifyDeleteAccount: builder.mutation<void, { email: string; token: string }>({
      query: (data) => ({
        url: USER_ROUTES.DELETE_ACCOUNT,
        method: 'POST',
        data,
      }),
    }),

    deleteAccount: builder.mutation<void, void>({
      query: () => ({
        url: USER_ROUTES.DELETE_ACCOUNT,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useGetUserExperiencesQuery,
  useAddUserExperienceMutation,
  useDeleteUserExperienceMutation,
  useUpdateUserExperienceMutation,
  useGetUserEducationsQuery,
  useAddUserEducationMutation,
  useDeleteUserEducationMutation,
  useUpdateUserEducationMutation,
  useAddUserCertificationMutation,
  useDeleteUserCertificationMutation,
  useGetUserCertificationsQuery,
  useUpdateUserCertificationMutation,
  useUpdateUserProfileMutation,
  useUpdateUserProfilePicMutation,
  useRequestDeleteAccountTokenMutation,
  useVerifyDeleteAccountMutation,
  useDeleteAccountMutation,
} = userApi;
